import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Lead from '../../entities/lead';
import Matter from '../../entities/matter';
import Invoice from '../../entities/invoice';
import Task from '../../entities/task';
import Answer from '../../entities/answer';
import Comment from '../../entities/comment';
import Conflict from '../../entities/conflict';
import Document from '../../entities/document';
import Event from '../../entities/event';
import Milestone from '../../entities/milestone';
import Participant from '../../entities/participant';
import RedFlag from '../../entities/redFlag';
import Referral from '../../entities/referral';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteAnswer, collectAnswerDeleteImpact} from './answerDeps';
import {deleteComment, collectCommentDeleteImpact} from './commentDeps';
import {deleteDocument, collectDocumentDeleteImpact} from './documentDeps';
import {deleteEvent, collectEventDeleteImpact} from './eventDeps';
import {deleteMilestone, collectMilestoneDeleteImpact} from './milestoneDeps';
import {deleteParticipant, collectParticipantDeleteImpact} from './participantDeps';
import {deleteRedFlag, collectRedFlagDeleteImpact} from './redFlagDeps';
import {deleteReferral, collectReferralDeleteImpact} from './referralDeps';
export function canDeleteLead(): boolean {
return canCascadeDelete();
}
export function collectLeadDeleteImpact(lead: Lead, context: DeleteImpactContext): void {
const key = 'lead:' + lead.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const matterLeadIdImpact = workingRows<Matter>('matter', findAll<Matter>('matters')).filter(matterRef => matterRef.leadId === lead.id);
matterLeadIdImpact.forEach(matterRef => addDeleteImpact(context, 'matter:' + matterRef.id, 'matters'));
const invoiceLeadIdImpact = workingRows<Invoice>('invoice', findAll<Invoice>('invoices')).filter(invoiceRef => invoiceRef.leadId === lead.id && !(invoiceRef.invoiceStatus === 'F'));
invoiceLeadIdImpact.forEach(invoiceRef => addDeleteImpact(context, 'invoice:' + invoiceRef.id, 'invoices'));const taskTieInsImpact = workingRows<Task>('task', findAll<Task>('tasks')).filter(taskRef => taskRef.status !== 'D' && taskRef.hasOwnProperty('tieIns')).filter(taskRef => !!taskRef.tieIns?.some(f => {return 'Lead' === f.entity && f.id === lead.id;}));
taskTieInsImpact.forEach(task => addDeleteImpact(context, 'task:' + task.id, 'tasks'));workingRows<Answer>('answer', findAll<Answer>('answers')).filter(answer => answer.status !== 'D' && answer.entityType === 'lead' && answer.entityId === lead.id).forEach(answer => {
if (context.visited.has('answer:' + answer.id)) return;
addDeleteImpact(context, 'answer:' + answer.id, 'answers');
collectAnswerDeleteImpact(answer, context);
});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'lead' && comment.entityId === lead.id).forEach(comment => {
if (context.visited.has('comment:' + comment.id)) return;
addDeleteImpact(context, 'comment:' + comment.id, 'comments');
collectCommentDeleteImpact(comment, context);
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'lead' && document.entityId === lead.id).forEach(document => {
if (context.visited.has('document:' + document.id)) return;
addDeleteImpact(context, 'document:' + document.id, 'documents');
collectDocumentDeleteImpact(document, context);
});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'lead' && event.entityId === lead.id).forEach(event => {
if (context.visited.has('event:' + event.id)) return;
addDeleteImpact(context, 'event:' + event.id, 'events');
collectEventDeleteImpact(event, context);
});
workingRows<Milestone>('milestone', findAll<Milestone>('milestones')).filter(milestone => milestone.status !== 'D' && milestone.entityType === 'lead' && milestone.entityId === lead.id).forEach(milestone => {
if (context.visited.has('milestone:' + milestone.id)) return;
addDeleteImpact(context, 'milestone:' + milestone.id, 'milestones');
collectMilestoneDeleteImpact(milestone, context);
});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'lead' && participant.entityId === lead.id).forEach(participant => {
if (context.visited.has('participant:' + participant.id)) return;
addDeleteImpact(context, 'participant:' + participant.id, 'participants');
collectParticipantDeleteImpact(participant, context);
});
workingRows<RedFlag>('redFlag', findAll<RedFlag>('redFlags')).filter(redFlag => redFlag.status !== 'D' && redFlag.entityType === 'lead' && redFlag.entityId === lead.id).forEach(redFlag => {
if (context.visited.has('redFlag:' + redFlag.id)) return;
addDeleteImpact(context, 'redFlag:' + redFlag.id, 'redFlags');
collectRedFlagDeleteImpact(redFlag, context);
});
workingRows<Referral>('referral', findAll<Referral>('referrals')).filter(referral => referral.status !== 'D' && referral.referral === lead.id).forEach(referral => {
if (context.visited.has('referral:' + referral.id)) return;
addDeleteImpact(context, 'referral:' + referral.id, 'referrals');
collectReferralDeleteImpact(referral, context);
});
}
export function deleteImpactLead(lead: Lead): DeleteImpact {
const context = deleteImpactContext();
collectLeadDeleteImpact(lead, context);
return deleteImpactCounts(context);
}
export function deleteLead(lead: Lead, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteLead()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectLeadDeleteImpact(lead, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || lead.id};
}
const key = 'lead:' + lead.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<Answer>('answer', findAll<Answer>('answers')).filter(answer => answer.status !== 'D' && answer.entityType === 'lead' && answer.entityId === lead.id).forEach(answer => {deleteAnswer(answer, mergeId, cascade);});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'lead' && comment.entityId === lead.id).forEach(comment => {deleteComment(comment, mergeId, cascade);});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'lead' && document.entityId === lead.id).forEach(document => {deleteDocument(document, mergeId, cascade);});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'lead' && event.entityId === lead.id).forEach(event => {deleteEvent(event, mergeId, cascade);});
workingRows<Milestone>('milestone', findAll<Milestone>('milestones')).filter(milestone => milestone.status !== 'D' && milestone.entityType === 'lead' && milestone.entityId === lead.id).forEach(milestone => {deleteMilestone(milestone, mergeId, cascade);});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'lead' && participant.entityId === lead.id).forEach(participant => {deleteParticipant(participant, mergeId, cascade);});
workingRows<RedFlag>('redFlag', findAll<RedFlag>('redFlags')).filter(redFlag => redFlag.status !== 'D' && redFlag.entityType === 'lead' && redFlag.entityId === lead.id).forEach(redFlag => {deleteRedFlag(redFlag, mergeId, cascade);});
workingRows<Referral>('referral', findAll<Referral>('referrals')).filter(referral => referral.status !== 'D' && referral.referral === lead.id).forEach(referral => {deleteReferral(referral, mergeId, cascade);});
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('leadId', lead.id)]).filter(matterTemp => matterTemp.leadId === lead.id).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
if (_matter.leadId === lead.id) {
_matter.leadId = null;

}
_work.set('matter:' + _matter.id, _matter);
dispatch(Actions.MatterActions.saveMatterQueue(_matter), 'matter:' + _matter.id);
});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('leadId', lead.id)]).filter(invoiceTemp => invoiceTemp.leadId === lead.id).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
if (_invoice.leadId === lead.id) {
_invoice.leadId = null;

}
_work.set('invoice:' + _invoice.id, _invoice);
dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice), 'invoice:' + _invoice.id);
});workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns')).filter(taskTemp => !!taskTemp.tieIns?.some(f => {return 'Lead' === f.entity && f.id === lead.id;})).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.tieIns = [...(_task.tieIns || [])].filter(f => !('Lead' === f.entity && f.id === lead.id));
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
dispatch(Actions.TaskActions.saveTaskQueue(_task), 'task:' + _task.id);
});
const deleted = lead.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.LeadActions.removeLeadQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceLead(lead: Lead, replaceWith: Lead, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = lead.id;}
const _work = workingSet();
workingRows<Matter>('matter', [...store.getState().matters.getMappingFor('leadId', lead.id)]).filter(matterTemp => matterTemp.leadId === lead.id).forEach(matterTemp => {
const _matter = ((_work.get('matter:' + matterTemp.id) ?? matterTemp) as Matter).clone();
if (_matter.status === 'D') return;
_matter.status = 'U';
_matter.addedOn = newHRTimestamp();
_matter.addedBy = getCurrentUserId();
_matter.importSource = targetImportSource;
_matter.importId = mergeId;
if (_matter.leadId === lead.id) {
_matter.leadId = replaceWith.id;

}
_work.set('matter:' + _matter.id, _matter);
store.dispatch(Actions.MatterActions.saveMatterQueue(_matter));
});
workingRows<Invoice>('invoice', [...store.getState().invoices.getMappingFor('leadId', lead.id)]).filter(invoiceTemp => invoiceTemp.leadId === lead.id).forEach(invoiceTemp => {
const _invoice = ((_work.get('invoice:' + invoiceTemp.id) ?? invoiceTemp) as Invoice).clone();
if (_invoice.status === 'D') return;
if (_invoice.invoiceStatus === 'F') return;
_invoice.status = 'U';
_invoice.addedOn = newHRTimestamp();
_invoice.addedBy = getCurrentUserId();
_invoice.importSource = targetImportSource;
_invoice.importId = mergeId;
if (_invoice.leadId === lead.id) {
_invoice.leadId = replaceWith.id;

}
_work.set('invoice:' + _invoice.id, _invoice);
store.dispatch(Actions.InvoiceActions.saveInvoiceQueue(_invoice));
});


workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns')).filter(taskTemp => !!taskTemp.tieIns?.some(f => {return 'Lead' === f.entity && f.id === lead.id;})).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.tieIns = [...(_task.tieIns || [])].map(f => {if ('Lead' === f.entity && f.id === lead.id) {const _f = {...f}; _f.id = replaceWith.id; return _f;} else return f;});
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'lead' && comment.entityId === lead.id).forEach(commentTemp => {
const _comment = ((_work.get('comment:' + commentTemp.id) ?? commentTemp) as Comment).clone();
if (_comment.status === 'D') return;
_comment.entityId = replaceWith.id;
_comment.status = 'U';
_comment.addedOn = newHRTimestamp();
_comment.addedBy = getCurrentUserId();
_comment.importSource = targetImportSource;
_comment.importId = mergeId;
_work.set('comment:' + _comment.id, _comment);
store.dispatch(Actions.CommentActions.saveCommentQueue(_comment));
});
workingRows<Conflict>('conflict', findAll<Conflict>('conflicts')).filter(conflict => conflict.status !== 'D' && conflict.entityType === 'lead' && conflict.entityId === lead.id).forEach(conflictTemp => {
const _conflict = ((_work.get('conflict:' + conflictTemp.id) ?? conflictTemp) as Conflict).clone();
if (_conflict.status === 'D') return;
_conflict.entityId = replaceWith.id;
_conflict.status = 'U';
_conflict.addedOn = newHRTimestamp();
_conflict.addedBy = getCurrentUserId();
_conflict.importSource = targetImportSource;
_conflict.importId = mergeId;
_work.set('conflict:' + _conflict.id, _conflict);
store.dispatch(Actions.ConflictActions.saveConflictQueue(_conflict));
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'lead' && document.entityId === lead.id).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.entityId = replaceWith.id;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_work.set('document:' + _document.id, _document);
store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
});
workingRows<Event>('event', findAll<Event>('events')).filter(event => event.status !== 'D' && event.entityType === 'lead' && event.entityId === lead.id).forEach(eventTemp => {
const _event = ((_work.get('event:' + eventTemp.id) ?? eventTemp) as Event).clone();
if (_event.status === 'D') return;
_event.entityId = replaceWith.id;
_event.status = 'U';
_event.addedOn = newHRTimestamp();
_event.addedBy = getCurrentUserId();
_event.importSource = targetImportSource;
_event.importId = mergeId;
_work.set('event:' + _event.id, _event);
store.dispatch(Actions.EventActions.saveEventQueue(_event));
});
workingRows<Milestone>('milestone', findAll<Milestone>('milestones')).filter(milestone => milestone.status !== 'D' && milestone.entityType === 'lead' && milestone.entityId === lead.id).forEach(milestoneTemp => {
const _milestone = ((_work.get('milestone:' + milestoneTemp.id) ?? milestoneTemp) as Milestone).clone();
if (_milestone.status === 'D') return;
_milestone.entityId = replaceWith.id;
_milestone.status = 'U';
_milestone.addedOn = newHRTimestamp();
_milestone.addedBy = getCurrentUserId();
_milestone.importSource = targetImportSource;
_milestone.importId = mergeId;
_work.set('milestone:' + _milestone.id, _milestone);
store.dispatch(Actions.MilestoneActions.saveMilestoneQueue(_milestone));
});
workingRows<Participant>('participant', findAll<Participant>('participants')).filter(participant => participant.status !== 'D' && participant.entityType === 'lead' && participant.entityId === lead.id).forEach(participantTemp => {
const _participant = ((_work.get('participant:' + participantTemp.id) ?? participantTemp) as Participant).clone();
if (_participant.status === 'D') return;
_participant.entityId = replaceWith.id;
_participant.status = 'U';
_participant.addedOn = newHRTimestamp();
_participant.addedBy = getCurrentUserId();
_participant.importSource = targetImportSource;
_participant.importId = mergeId;
_work.set('participant:' + _participant.id, _participant);
store.dispatch(Actions.ParticipantActions.saveParticipantQueue(_participant));
});
workingRows<RedFlag>('redFlag', findAll<RedFlag>('redFlags')).filter(redFlag => redFlag.status !== 'D' && redFlag.entityType === 'lead' && redFlag.entityId === lead.id).forEach(redFlagTemp => {
const _redFlag = ((_work.get('redFlag:' + redFlagTemp.id) ?? redFlagTemp) as RedFlag).clone();
if (_redFlag.status === 'D') return;
_redFlag.entityId = replaceWith.id;
_redFlag.status = 'U';
_redFlag.addedOn = newHRTimestamp();
_redFlag.addedBy = getCurrentUserId();
_redFlag.importSource = targetImportSource;
_redFlag.importId = mergeId;
_work.set('redFlag:' + _redFlag.id, _redFlag);
store.dispatch(Actions.RedFlagActions.saveRedFlagQueue(_redFlag));
});
workingRows<Referral>('referral', findAll<Referral>('referrals')).filter(referral => referral.status !== 'D' && referral.referral === lead.id).forEach(referralTemp => {
const _referral = ((_work.get('referral:' + referralTemp.id) ?? referralTemp) as Referral).clone();
if (_referral.status === 'D') return;
_referral.referral = replaceWith.id;
_referral.status = 'U';
_referral.addedOn = newHRTimestamp();
_referral.addedBy = getCurrentUserId();
_referral.importSource = targetImportSource;
_referral.importId = mergeId;
_work.set('referral:' + _referral.id, _referral);
store.dispatch(Actions.ReferralActions.saveReferralQueue(_referral));
});
return lead;
}
